import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 h-[72px] flex items-center transition-all duration-300 ${
          scrolled ? 'bg-background-50 border-b border-background-200/70' : 'bg-transparent'
        }`}
      >
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5 whitespace-nowrap cursor-pointer"
          >
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-file-list-3-line text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-foreground-950 text-lg font-semibold tracking-tight">
              Superbuy Spread
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-label tracking-wide uppercase">Home</Link>
            <Link to="/superbuy-sheet-guide" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-label tracking-wide uppercase">Guide</Link>
            <Link to="/how-to-use-superbuy-sheet" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-label tracking-wide uppercase">Tutorial</Link>
            <Link to="/superbuy-shipping-strategy-guide" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-label tracking-wide uppercase">Shipping</Link>
            <a href="#testimonials" className="text-sm text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap font-label tracking-wide uppercase">Reviews</a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-sm font-label tracking-wide uppercase whitespace-nowrap cursor-pointer transition-all hover:bg-foreground-800"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-[280px] bg-foreground-950 rounded-2xl py-3 shadow-lg z-50 animate-fade-in-up">
                  <div className="max-h-[420px] overflow-y-auto px-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2.5 text-sm text-background-100 hover:bg-foreground-800 rounded-xl transition-colors whitespace-nowrap cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap cursor-pointer transition-all hover:bg-primary-600"
            >
              superbuy sheet
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-foreground-950 text-foreground-950 text-sm font-label font-medium whitespace-nowrap cursor-pointer transition-all hover:bg-foreground-950 hover:text-background-50"
            >
              buy it now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 rounded-full flex items-center justify-center cursor-pointer border border-foreground-300 hover:border-foreground-950 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <i className="ri-close-line text-foreground-950 text-xl"></i>
              ) : (
                <i className="ri-menu-line text-foreground-950 text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      <div
        ref={mobileRef}
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm"
          onClick={closeMobile}
        ></div>

        <div
          className={`absolute top-0 right-0 w-[320px] max-w-[85vw] h-full bg-background-50 transition-transform duration-300 overflow-y-auto ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="pt-[88px] pb-8 px-6">
            <div className="flex flex-col gap-1 mb-8">
              <p className="text-xs font-label uppercase tracking-[2px] text-foreground-400 mb-3 px-2">Navigation</p>
              <Link
                to="/"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer font-label"
              >
                <i className="ri-home-4-line text-foreground-400 text-lg"></i>
                Home
              </Link>
              <Link
                to="/superbuy-sheet-guide"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer font-label"
              >
                <i className="ri-book-open-line text-foreground-400 text-lg"></i>
                Ultimate Guide
              </Link>
              <Link
                to="/how-to-use-superbuy-sheet"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer font-label"
              >
                <i className="ri-file-text-line text-foreground-400 text-lg"></i>
                How To Use Tutorial
              </Link>
              <Link
                to="/superbuy-shipping-strategy-guide"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer font-label"
              >
                <i className="ri-ship-line text-foreground-400 text-lg"></i>
                Shipping Strategy
              </Link>
              <a
                href="#testimonials"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer font-label"
              >
                <i className="ri-star-line text-foreground-400 text-lg"></i>
                Reviews
              </a>
            </div>

            <div className="border-t border-background-200/70 pt-6 mb-8">
              <p className="text-xs font-label uppercase tracking-[2px] text-foreground-400 mb-3 px-2">14 Categories</p>
              <div className="flex flex-col gap-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-foreground-300 flex-shrink-0"></span>
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-6 flex flex-col gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium whitespace-nowrap cursor-pointer transition-all hover:bg-primary-600"
              >
                superbuy sheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-full border-2 border-foreground-950 text-foreground-950 text-sm font-label font-medium whitespace-nowrap cursor-pointer transition-all hover:bg-foreground-950 hover:text-background-50"
              >
                buy it now
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}